import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type CSSProperties,
  type ReactElement,
} from 'react';
import { Link } from 'react-router-dom';
import {
  v2,
  ApiError,
  parseApiErrorBody,
  type ApiErrorBody,
  type HealthDetailed,
  type MergedSettingsMap,
} from '@/api/v2Client';
import { Spinner } from '@/components/dashboard/Spinner';
import { useUserPermissions } from '@/hooks/useUserPermissions';
import { PermissionDenied } from '@/pages/PermissionDenied';

// Phase 5.19.2 — operator-facing view of Phase 5.19's
// `/api/v1/health/detailed` endpoint. No auto-poll by default; an opt-in
// toggle adds a 15-second `setInterval`. Manual Refresh button is the
// primary affordance.

const AUTO_REFRESH_INTERVAL_MS = 15_000;

const pageStyle: CSSProperties = { padding: '24px 32px', maxWidth: 900 };
const subtitleStyle: CSSProperties = {
  color: 'var(--text-muted)',
  marginTop: 4,
  marginBottom: 24,
};
const toolbarStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 12,
  flexWrap: 'wrap',
  marginBottom: 24,
};
const refreshButtonStyle: CSSProperties = {
  background: 'var(--accent)',
  color: '#fff',
  border: 'none',
  borderRadius: 6,
  padding: '8px 16px',
  fontSize: 13,
  fontWeight: 600,
  cursor: 'pointer',
};
const refreshButtonDisabledStyle: CSSProperties = {
  ...refreshButtonStyle,
  background: 'var(--surface2)',
  color: 'var(--text-muted)',
  cursor: 'wait',
};
const toggleLabelStyle: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
  fontSize: 13,
  color: 'var(--text-muted)',
  cursor: 'pointer',
};
const lastFetchedStyle: CSSProperties = {
  fontSize: 12,
  color: 'var(--text-muted)',
};

const pillRowStyle: CSSProperties = {
  display: 'flex',
  flexWrap: 'wrap',
  gap: 12,
  marginBottom: 24,
};

const pillBaseStyle: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 8,
  padding: '6px 12px',
  borderRadius: 999,
  fontSize: 13,
  fontWeight: 600,
};

const metadataGridStyle: CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'max-content 1fr',
  rowGap: 8,
  columnGap: 24,
  fontSize: 13,
  marginBottom: 16,
  background: 'var(--surface)',
  border: '1px solid var(--border)',
  borderRadius: 8,
  padding: '16px 20px',
};

const metadataLabelStyle: CSSProperties = {
  color: 'var(--text-muted)',
  fontWeight: 600,
};

const metadataValueStyle: CSSProperties = {
  fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
  wordBreak: 'break-all',
};

const errorBannerStyle: CSSProperties = {
  background: 'var(--red-bg, #FEE2E2)',
  color: 'var(--red, #B91C1C)',
  border: '1px solid var(--red, #B91C1C)',
  borderRadius: 6,
  padding: '12px 16px',
  marginBottom: 16,
  display: 'flex',
  flexWrap: 'wrap',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 12,
};

// T-10 (D-35) — the long latest-migration filename used to be a single
// unbreakable monospace token inside a no-wrap pill, so at phone width it
// pushed the page into horizontal scroll. The pill may now wrap and the
// filename breaks anywhere; the full name stays in a tooltip.
const migrationLatestStyle: CSSProperties = {
  opacity: 0.85,
  fontWeight: 400,
  fontFamily: 'ui-monospace, monospace',
  overflowWrap: 'anywhere',
  wordBreak: 'break-all',
  minWidth: 0,
};

// T-10 (D-34) — the page and the on-call panel show a PLAIN sentence on
// failure. The server body (which may be raw JSON, or a proxy's text) goes
// to the console only — it used to be rendered verbatim, because
// ApiError.message IS the raw response body.
export const HEALTH_LOAD_FAILED = "Couldn't load system health. Try again.";
export const HEALTH_DB_UNREACHABLE =
  "The database is unreachable, so system health can't be checked right now. Try again.";
export const ONCALL_LOAD_FAILED = "Couldn't load on-call info. Try again.";

function isHealthBody(v: unknown): v is HealthDetailed {
  if (!v || typeof v !== 'object') return false;
  const o = v as Record<string, unknown>;
  return (
    (o['status'] === 'healthy' || o['status'] === 'degraded') &&
    typeof o['db'] === 'object' &&
    o['db'] !== null &&
    typeof o['migrations'] === 'object' &&
    o['migrations'] !== null
  );
}

type HealthFailure =
  | { kind: 'body'; data: HealthDetailed }
  | { kind: 'forbidden' }
  | { kind: 'database' }
  | { kind: 'other' };

/**
 * Sort a failed health fetch into what the page should do with it:
 *  - a 5xx that still carries a valid health body (e.g. a 503 "degraded")
 *    renders the pills — a degraded system is data, not an error;
 *  - 403 → the standard Access denied view;
 *  - 503 `DatabaseUnavailable` (the server could not even check the
 *    permission) → say the database is unreachable, plainly;
 *  - anything else → the generic plain sentence.
 */
function classifyHealthFailure(err: unknown): HealthFailure {
  if (!(err instanceof ApiError)) return { kind: 'other' };
  if (err.status === 403) return { kind: 'forbidden' };
  const parsed = parseApiErrorBody(err) as (ApiErrorBody & { ok?: unknown }) | null;
  if (err.status >= 500 && parsed?.ok === true && isHealthBody(parsed.data)) {
    return { kind: 'body', data: parsed.data };
  }
  if (err.status === 503 && parsed?.error === 'DatabaseUnavailable') return { kind: 'database' };
  return { kind: 'other' };
}

const errorRetryButtonStyle: CSSProperties = {
  ...refreshButtonStyle,
  background: 'var(--red, #B91C1C)',
};

const helpNoteStyle: CSSProperties = {
  fontSize: 12,
  color: 'var(--text-muted)',
  marginLeft: 6,
  fontStyle: 'italic',
};

// Phase 5.19.5 — on-call contact panel styles. Inline to match the
// existing inline-style convention of this page; no new CSS file added.
const oncallPanelStyle: CSSProperties = {
  background: 'var(--surface)',
  border: '1px solid var(--border)',
  borderRadius: 8,
  padding: '16px 20px',
  marginTop: 24,
};
const oncallHeadingStyle: CSSProperties = {
  margin: 0,
  marginBottom: 12,
  fontSize: 16,
};
const oncallRowStyle: CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'max-content 1fr',
  rowGap: 6,
  columnGap: 16,
  fontSize: 13,
};
const oncallLabelStyle: CSSProperties = {
  color: 'var(--text-muted)',
  fontWeight: 600,
};
const oncallEmptyStyle: CSSProperties = {
  margin: 0,
  fontSize: 13,
  color: 'var(--text-muted)',
};
const oncallErrorStyle: CSSProperties = {
  margin: 0,
  fontSize: 13,
  color: 'var(--red, #B91C1C)',
};
const oncallErrorRowStyle: CSSProperties = {
  display: 'flex',
  flexWrap: 'wrap',
  alignItems: 'center',
  gap: 12,
};

/** Pill colors keyed off observed state. Inline rather than CSS classes
 *  to match the SettingsPage / SpecFilesPage convention. */
function overallPillStyle(status: HealthDetailed['status']): CSSProperties {
  if (status === 'healthy') {
    return {
      ...pillBaseStyle,
      background: 'var(--green-bg, #DCFCE7)',
      color: 'var(--green, #15803D)',
      border: '1px solid var(--green, #15803D)',
    };
  }
  return {
    ...pillBaseStyle,
    background: 'var(--amber-bg, #FEF3C7)',
    color: 'var(--amber, #B45309)',
    border: '1px solid var(--amber, #B45309)',
  };
}

function dbPillStyle(status: HealthDetailed['db']['status']): CSSProperties {
  if (status === 'up') {
    return {
      ...pillBaseStyle,
      background: 'var(--green-bg, #DCFCE7)',
      color: 'var(--green, #15803D)',
      border: '1px solid var(--green, #15803D)',
    };
  }
  return {
    ...pillBaseStyle,
    background: 'var(--red-bg, #FEE2E2)',
    color: 'var(--red, #B91C1C)',
    border: '1px solid var(--red, #B91C1C)',
  };
}

const migrationsPillStyle: CSSProperties = {
  ...pillBaseStyle,
  flexWrap: 'wrap',
  maxWidth: '100%',
  minWidth: 0,
  boxSizing: 'border-box',
  background: 'var(--surface2)',
  color: 'var(--text)',
  border: '1px solid var(--border)',
};

// TODO-100 — colour mapping for the ESG / Auth / Storage status pills.
// Mirrors dbPillStyle's green/red treatment with an additional 'unknown'
// (grey) state so config-pending environments don't false-positive as down.
function tristatePillStyle(
  status: 'up' | 'down' | 'unknown' | 'not_configured',
): CSSProperties {
  if (status === 'up') {
    return {
      ...pillBaseStyle,
      background: 'var(--green-bg, #DCFCE7)',
      color: 'var(--green, #15803D)',
      border: '1px solid var(--green, #15803D)',
    };
  }
  if (status === 'down') {
    return {
      ...pillBaseStyle,
      background: 'var(--red-bg, #FEE2E2)',
      color: 'var(--red, #B91C1C)',
      border: '1px solid var(--red, #B91C1C)',
    };
  }
  // unknown / not_configured — neutral grey
  return {
    ...pillBaseStyle,
    background: 'var(--surface2)',
    color: 'var(--text-muted)',
    border: '1px solid var(--border)',
  };
}

/**
 * Parse the timestamp prefix from a migration filename like
 * "20251101120000_add_specfiles_table.sql" → Date(2025-11-01 12:00:00 UTC).
 * Returns null if the prefix doesn't match the expected format.
 */
function parseMigrationTimestamp(
  filename: string | null | undefined,
): Date | null {
  if (!filename) return null;
  const match = filename.match(/^(\d{4})(\d{2})(\d{2})(\d{2})(\d{2})(\d{2})_/);
  if (!match) return null;
  const [, y, mo, d, h, mi, s] = match;
  const utcMs = Date.UTC(
    Number(y),
    Number(mo) - 1,
    Number(d),
    Number(h),
    Number(mi),
    Number(s),
  );
  return new Date(utcMs);
}

/**
 * Format a startedAt ISO timestamp as a human-readable "X minutes ago" /
 * "X hours ago" relative to `now`. Falls back to the raw ISO when the
 * delta is non-numeric (clock skew, etc.).
 */
function formatStartedAgo(startedAt: string, nowIso: string): string {
  const t0 = Date.parse(startedAt);
  const t1 = Date.parse(nowIso);
  if (!Number.isFinite(t0) || !Number.isFinite(t1) || t1 < t0) return startedAt;
  const seconds = Math.floor((t1 - t0) / 1000);
  if (seconds < 60) return `${seconds} second${seconds === 1 ? '' : 's'} ago`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes} minute${minutes === 1 ? '' : 's'} ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} hour${hours === 1 ? '' : 's'} ago`;
  const days = Math.floor(hours / 24);
  return `${days} day${days === 1 ? '' : 's'} ago`;
}

// Phase 5.19.5 — read a string value from the merged settings map by key.
// Settings values are `unknown` because the 3-tier merge doesn't enforce
// per-key types; on-call keys are conventionally strings. Anything that
// isn't a non-empty string is treated as "not set" — including numbers,
// booleans, objects, and whitespace-only strings.
function getSettingString(settings: MergedSettingsMap, key: string): string | null {
  const entry = settings[key];
  if (!entry) return null;
  const v = entry.Value;
  if (typeof v !== 'string') return null;
  const trimmed = v.trim();
  return trimmed.length > 0 ? trimmed : null;
}

// Phase 5.19.5 — inline panel rendering on-call contact info. Tolerant
// of missing keys (renders only what's set; "not configured" state when
// nothing is set) and tolerant of malformed values (rendered as-is per
// HARD GUARDRAIL #6 — validation lives in the Settings save flow).
function OnCallPanel({ settings }: { readonly settings: MergedSettingsMap }): ReactElement {
  const primaryName = getSettingString(settings, 'oncall.primary.name');
  const primaryEmail = getSettingString(settings, 'oncall.primary.email');
  const secondaryName = getSettingString(settings, 'oncall.secondary.name');
  const secondaryEmail = getSettingString(settings, 'oncall.secondary.email');
  const runbookUrl = getSettingString(settings, 'oncall.runbook.url');
  const escalationEmail = getSettingString(settings, 'oncall.escalation.email');

  const anySet =
    primaryName !== null ||
    primaryEmail !== null ||
    secondaryName !== null ||
    secondaryEmail !== null ||
    runbookUrl !== null ||
    escalationEmail !== null;

  if (!anySet) {
    return (
      <p style={oncallEmptyStyle} data-testid="health-oncall-empty">
        On-call contact is not configured.{' '}
        <Link to="/admin/settings">Set it up in Admin → Settings</Link> using keys
        prefixed with <code>oncall.</code>.
      </p>
    );
  }

  const rows: Array<{ key: string; label: string; node: ReactElement }> = [];
  if (primaryName !== null || primaryEmail !== null) {
    rows.push({
      key: 'primary',
      label: 'Primary',
      node: (
        <span data-testid="health-oncall-primary">
          {primaryName ?? '—'}
          {primaryEmail !== null && (
            <>
              {' · '}
              <a href={`mailto:${primaryEmail}`} data-testid="health-oncall-primary-email">
                {primaryEmail}
              </a>
            </>
          )}
        </span>
      ),
    });
  }
  if (secondaryName !== null || secondaryEmail !== null) {
    rows.push({
      key: 'secondary',
      label: 'Secondary',
      node: (
        <span data-testid="health-oncall-secondary">
          {secondaryName ?? '—'}
          {secondaryEmail !== null && (
            <>
              {' · '}
              <a href={`mailto:${secondaryEmail}`} data-testid="health-oncall-secondary-email">
                {secondaryEmail}
              </a>
            </>
          )}
        </span>
      ),
    });
  }
  if (escalationEmail !== null) {
    rows.push({
      key: 'escalation',
      label: 'Escalation',
      node: (
        <a href={`mailto:${escalationEmail}`} data-testid="health-oncall-escalation-email">
          {escalationEmail}
        </a>
      ),
    });
  }
  if (runbookUrl !== null) {
    rows.push({
      key: 'runbook',
      label: 'Runbook',
      node: (
        <a
          href={runbookUrl}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="health-oncall-runbook"
        >
          {runbookUrl} ↗
        </a>
      ),
    });
  }

  return (
    <dl style={oncallRowStyle} data-testid="health-oncall-rows">
      {rows.map((r) => (
        <div key={r.key} style={{ display: 'contents' }}>
          <dt style={oncallLabelStyle}>{r.label}</dt>
          <dd style={{ margin: 0, minWidth: 0, overflowWrap: 'anywhere' }}>{r.node}</dd>
        </div>
      ))}
    </dl>
  );
}

// phase-role-access-audit-and-full-role-coverage (F4) — this page had NO
// permission gate: the menu offered it only under AuditLog:read, but any
// role could reach the full health dashboard by direct URL. The gate
// wrapper enforces the same permission the menu (and Assistant Activity,
// its sibling observability surface) uses; the body keeps its hooks
// unconditional (rules-of-hooks). The backend endpoint stays auth-only by
// the deliberate Phase 5.19 decision — flagged to Sharon in the report.
export function SystemHealthPage(): ReactElement {
  const perms = useUserPermissions();
  if (perms.loading) return <Spinner caption="Checking permissions…" />;
  // T-10 — UNKNOWN permissions (the /users/me lookup itself failed, which is
  // exactly what a database outage does) are not a denial. Render the body
  // and let the server decide: it answers 403 → Access denied, or 503
  // DatabaseUnavailable → the plain "database is unreachable" sentence.
  if (!perms.failed && !perms.hasPermission('AuditLog', 'read')) return <PermissionDenied />;
  return <SystemHealthPageBody />;
}

function SystemHealthPageBody(): ReactElement {
  const [data, setData] = useState<HealthDetailed | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [forbidden, setForbidden] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(true);
  const [autoRefresh, setAutoRefresh] = useState<boolean>(false);
  const [lastFetchedAt, setLastFetchedAt] = useState<string | null>(null);
  // Phase 5.19.5 — on-call panel state. Independent of health state so
  // a settings backend failure doesn't mask health-data display and
  // vice versa.
  const [settings, setSettings] = useState<MergedSettingsMap | null>(null);
  const [settingsError, setSettingsError] = useState<string | null>(null);
  // Tracks the most-recent in-flight request so a slow first response can't
  // overwrite a faster second response.
  const requestSeqRef = useRef(0);
  const settingsSeqRef = useRef(0);

  const fetchHealth = useCallback(async (): Promise<void> => {
    const seq = ++requestSeqRef.current;
    setLoading(true);
    try {
      const res = await v2.health.detailed();
      if (requestSeqRef.current !== seq) return;
      setData(res);
      setError(null);
      setLastFetchedAt(new Date().toISOString());
    } catch (err) {
      if (requestSeqRef.current !== seq) return;
      const failure = classifyHealthFailure(err);
      if (failure.kind === 'body') {
        setData(failure.data);
        setError(null);
        setLastFetchedAt(new Date().toISOString());
        return;
      }
      console.error('[SystemHealth] health fetch failed', err);
      if (failure.kind === 'forbidden') {
        setForbidden(true);
        return;
      }
      setError(failure.kind === 'database' ? HEALTH_DB_UNREACHABLE : HEALTH_LOAD_FAILED);
    } finally {
      if (requestSeqRef.current === seq) setLoading(false);
    }
  }, []);

  // Phase 5.19.5 — fetch the merged settings for the on-call panel.
  // Independent of fetchHealth: a failure here sets `settingsError` but
  // does NOT clear health `data` or set the page-level `error`.
  const fetchSettings = useCallback(async (): Promise<void> => {
    const seq = ++settingsSeqRef.current;
    try {
      const res = await v2.settings.listAll();
      if (settingsSeqRef.current !== seq) return;
      setSettings(res.settings);
      setSettingsError(null);
    } catch (err) {
      if (settingsSeqRef.current !== seq) return;
      console.error('[SystemHealth] on-call settings fetch failed', err);
      setSettingsError(ONCALL_LOAD_FAILED);
    }
  }, []);

  // Auto-load on mount. Both fetches fire in parallel — neither awaits
  // the other (Promise.all-style without the unified rejection path,
  // since each has its own error state).
  useEffect(() => {
    void fetchHealth();
    void fetchSettings();
  }, [fetchHealth, fetchSettings]);

  // Phase 5.19.5 — Refresh button + initial-mount path fetch BOTH;
  // the auto-refresh interval fires ONLY fetchHealth (per HARD
  // GUARDRAIL #7: do not poll settings on a refresh interval).
  const handleRefresh = useCallback((): void => {
    void fetchHealth();
    void fetchSettings();
  }, [fetchHealth, fetchSettings]);

  // Optional polling. Toggle defaults OFF; when ON, fires every 15s.
  useEffect(() => {
    if (!autoRefresh) return undefined;
    const id = globalThis.setInterval(() => {
      void fetchHealth();
    }, AUTO_REFRESH_INTERVAL_MS);
    return () => {
      globalThis.clearInterval(id);
    };
  }, [autoRefresh, fetchHealth]);

  const showInitialSpinner = loading && data === null && error === null;

  // T-10 — the server refused (403): the same view as the page gate, not a
  // red banner with the server's JSON in it.
  if (forbidden) return <PermissionDenied />;

  return (
    <section style={pageStyle}>
      <h1 style={{ margin: 0 }}>System Health</h1>
      <p style={subtitleStyle}>Live status of the backend services.</p>

      <div style={toolbarStyle}>
        <button
          type="button"
          onClick={handleRefresh}
          disabled={loading}
          data-testid="health-refresh-btn"
          style={loading ? refreshButtonDisabledStyle : refreshButtonStyle}
        >
          {loading ? 'Refreshing…' : 'Refresh'}
        </button>
        <label style={toggleLabelStyle}>
          <input
            type="checkbox"
            checked={autoRefresh}
            onChange={(e) => setAutoRefresh(e.target.checked)}
            data-testid="health-auto-refresh-toggle"
          />
          Auto-refresh every 15s
        </label>
        {lastFetchedAt && (
          <span style={lastFetchedStyle} data-testid="health-last-fetched">
            Last fetched {new Date(lastFetchedAt).toLocaleTimeString()}
          </span>
        )}
      </div>

      {error && (
        <div role="alert" style={errorBannerStyle} data-testid="health-error">
          <span>{error}</span>
          <button
            type="button"
            onClick={() => void fetchHealth()}
            style={errorRetryButtonStyle}
          >
            Retry
          </button>
        </div>
      )}

      {showInitialSpinner && <Spinner caption="Loading system health…" />}

      {data && (
        <>
          <div style={pillRowStyle}>
            <span
              style={overallPillStyle(data.status)}
              data-testid="health-pill-overall"
            >
              {data.status === 'healthy' ? '✓ Healthy' : '⚠ Degraded'}
            </span>
            <span style={dbPillStyle(data.db.status)} data-testid="health-pill-db">
              {data.db.status === 'up' ? (
                <>
                  DB Up
                  {typeof data.db.latencyMs === 'number' && (
                    <span style={{ opacity: 0.85, fontWeight: 400 }}>
                      ({data.db.latencyMs}ms)
                    </span>
                  )}
                </>
              ) : (
                'DB Down'
              )}
            </span>
            <span
              style={migrationsPillStyle}
              data-testid="health-pill-migrations"
            >
              {data.migrations.count !== null
                ? `${data.migrations.count} migration${data.migrations.count === 1 ? '' : 's'} applied`
                : 'Migrations unavailable'}
              {data.migrations.latest && (
                <span
                  style={migrationLatestStyle}
                  title={data.migrations.latest}
                  data-testid="health-migration-latest"
                >
                  · latest: {data.migrations.latest}
                </span>
              )}
              {(() => {
                const ts = parseMigrationTimestamp(data.migrations.latest);
                if (!ts) return null;
                return (
                  <span
                    style={{ opacity: 0.75, fontWeight: 400, marginLeft: 6 }}
                    data-testid="health-migration-applied-ago"
                  >
                    (applied {formatStartedAgo(ts.toISOString(), data.now)})
                  </span>
                );
              })()}
            </span>

            {/* TODO-100 — ESG gateway pill. Always rendered so an outage is
                immediately visible. Shows mode (stub/real) alongside status. */}
            <span
              style={tristatePillStyle(data.esg.status)}
              data-testid="health-pill-esg"
            >
              {data.esg.status === 'up' && (
                <>
                  ESG Up
                  <span style={{ opacity: 0.85, fontWeight: 400 }}>
                    ({data.esg.mode}
                    {typeof data.esg.latencyMs === 'number' && data.esg.latencyMs > 0
                      ? `, ${data.esg.latencyMs}ms`
                      : ''}
                    )
                  </span>
                </>
              )}
              {data.esg.status === 'down' && 'ESG Down'}
              {data.esg.status === 'unknown' && (
                <>
                  ESG Unknown
                  <span style={{ opacity: 0.85, fontWeight: 400 }}>
                    ({data.esg.mode})
                  </span>
                </>
              )}
            </span>

            {/* TODO-100 — Auth / JWKS pill. In dev (AUTH_ENABLED=false) shows
                "Auth disabled (dev)" with a neutral grey treatment. */}
            <span
              style={
                data.auth.enabled
                  ? tristatePillStyle(data.auth.status)
                  : tristatePillStyle('unknown')
              }
              data-testid="health-pill-auth"
            >
              {!data.auth.enabled && 'Auth disabled (dev)'}
              {data.auth.enabled && data.auth.status === 'up' && (
                <>
                  Auth Up
                  {typeof data.auth.latencyMs === 'number' && (
                    <span style={{ opacity: 0.85, fontWeight: 400 }}>
                      ({data.auth.latencyMs}ms)
                    </span>
                  )}
                </>
              )}
              {data.auth.enabled && data.auth.status === 'down' && 'Auth Down'}
              {data.auth.enabled && data.auth.status === 'unknown' && 'Auth Unknown'}
            </span>

            {/* TODO-100 — Storage backend pill. Shows driver alongside status.
                'not_configured' is grey (vestigial pre-Azure-blob state). */}
            <span
              style={tristatePillStyle(data.storage.status)}
              data-testid="health-pill-storage"
            >
              {data.storage.status === 'up' && (
                <>
                  Storage Up
                  {data.storage.driver && (
                    <span style={{ opacity: 0.85, fontWeight: 400 }}>
                      ({data.storage.driver})
                    </span>
                  )}
                </>
              )}
              {data.storage.status === 'down' && (
                <>
                  Storage Down
                  {data.storage.driver && (
                    <span style={{ opacity: 0.85, fontWeight: 400 }}>
                      ({data.storage.driver})
                    </span>
                  )}
                </>
              )}
              {data.storage.status === 'not_configured' && 'Storage not configured'}
              {data.storage.status === 'unknown' && 'Storage Unknown'}
            </span>

            {/* Test #9 — the AI assistant's configuration, next to storage and
                the DB: key present / rejected by the provider / absent. */}
            <span
              style={tristatePillStyle(
                data.assistant.status === 'configured'
                  ? 'up'
                  : data.assistant.status === 'key_invalid'
                    ? 'down'
                    : 'not_configured',
              )}
              data-testid="health-pill-assistant"
            >
              {data.assistant.status === 'configured' && 'Assistant configured'}
              {data.assistant.status === 'key_invalid' && 'Assistant key rejected'}
              {data.assistant.status === 'not_configured' && 'Assistant not configured'}
            </span>
          </div>

          <dl style={metadataGridStyle} data-testid="health-metadata">
            <dt style={metadataLabelStyle}>Version</dt>
            <dd style={metadataValueStyle}>{data.version}</dd>

            <dt style={metadataLabelStyle}>Build SHA</dt>
            <dd style={metadataValueStyle}>
              {data.buildSha}
              {data.buildSha === 'unknown' && (
                <span style={helpNoteStyle} data-testid="health-buildsha-pending">
                  (commit SHA threaded after Phase 5.19.1)
                </span>
              )}
            </dd>

            <dt style={metadataLabelStyle}>Started</dt>
            <dd style={metadataValueStyle}>
              {formatStartedAgo(data.startedAt, data.now)}
              <span style={helpNoteStyle}>· {data.startedAt}</span>
            </dd>

            <dt style={metadataLabelStyle}>Server time</dt>
            <dd style={metadataValueStyle}>{data.now}</dd>

            <dt style={metadataLabelStyle}>Response time</dt>
            <dd style={metadataValueStyle}>{data.responseTimeMs}ms</dd>
          </dl>
        </>
      )}

      {/* Phase 5.19.5 — on-call contact panel. Renders independently of
          `data` so a health-failure scenario (most likely incident path)
          still surfaces the escalation contact. Renders its own loading
          / error / data sub-states; never gated on the global Spinner. */}
      <section style={oncallPanelStyle} data-testid="health-oncall-panel">
        <h2 style={oncallHeadingStyle}>On-Call Contact</h2>
        {settingsError !== null ? (
          <div role="alert" style={oncallErrorRowStyle}>
            <p style={oncallErrorStyle} data-testid="health-oncall-error">
              {settingsError}
            </p>
            <button
              type="button"
              onClick={() => void fetchSettings()}
              style={errorRetryButtonStyle}
              data-testid="health-oncall-retry"
            >
              Retry
            </button>
          </div>
        ) : settings === null ? (
          <p style={oncallEmptyStyle} data-testid="health-oncall-loading">
            Loading on-call info…
          </p>
        ) : (
          <OnCallPanel settings={settings} />
        )}
      </section>
    </section>
  );
}
